import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Scissors, Mail, Lock, ArrowLeft, Sparkles, Loader2 } from "lucide-react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard";

  // Auto-redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate(user.role === "stylist" ? "/stylist" : "/dashboard", { replace: true });
    }
  }, [user, navigate]);

  // Diverse professional images for the side panel
  const sideImages = [
    {
      src: "https://images.pexels.com/photos/7697673/pexels-photo-7697673.jpeg?auto=compress&w=800",
      alt: "Black barber giving a fresh fade"
    },
    {
      src: "https://images.pexels.com/photos/3993467/pexels-photo-3993467.jpeg?auto=compress&w=800",
      alt: "Black female stylist doing braids"
    },
    {
      src: "https://images.pexels.com/photos/3998429/pexels-photo-3998429.jpeg?auto=compress&w=800",
      alt: "Latino barber styling client"
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      navigate(user.role === "stylist" ? "/stylist" : "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    localStorage.removeItem("google_signup_role");
    // Skip the guide for returning users
    localStorage.setItem("myhairchair_guide_skipped", "true");
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMagicLink = async () => {
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }
    
    setMagicLinkLoading(true);
    try {
      await axios.post(`${API}/auth/magic-link`, { email });
      setMagicLinkSent(true);
      toast.success("Magic link sent! Check your email.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send magic link");
    } finally {
      setMagicLinkLoading(false);
    }
  };

  // Show magic link success state
  if (magicLinkSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <Mail className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-display text-3xl font-bold text-gray-800 mb-4">Check Your Email!</h1>
          <p className="text-gray-600 mb-6">
            We sent a magic link to <strong>{email}</strong>. Click the link to sign in instantly.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            Did not receive it? Check your spam folder or{" "}
            <button onClick={() => setMagicLinkSent(false)} className="text-orange-600 hover:underline">
              try again
            </button>
          </p>
          <Link to="/" className="text-gray-500 hover:text-orange-600">
            ← Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 py-12 lg:px-16 bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-orange-600 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 via-red-500 to-amber-400 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/30">
              <Scissors className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-bold text-xl text-gray-800">MyHairChair</span>
          </div>

          <h1 className="font-display text-3xl font-bold text-gray-800 mb-2">
            Welcome back
          </h1>
          <p className="text-gray-500 mb-8">
            Sign in to your account to continue
          </p>

          {/* Google Sign In Button - Primary/Fastest Option */}
          <Button
            type="button"
            className="w-full h-14 gap-3 mb-4 bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-200 hover:border-orange-300 shadow-lg"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="font-semibold">Continue with Google</span>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full ml-auto">Fastest</span>
          </Button>

          {/* Show email options toggle */}
          {!showEmailForm ? (
            <button
              onClick={() => setShowEmailForm(true)}
              className="w-full text-center text-gray-500 hover:text-orange-600 text-sm py-3 transition-colors"
            >
              Or use email instead →
            </button>
          ) : (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-orange-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-gradient-to-r from-orange-50 via-amber-50 to-yellow-50 text-gray-400">Or sign in with email</span>
                </div>
              </div>

              {/* Magic Link Option */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  <span className="font-medium text-purple-700 text-sm">Magic Link (No Password)</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 h-10 border-purple-200 focus:border-purple-400 bg-white"
                  />
                  <Button
                    type="button"
                    onClick={handleMagicLink}
                    disabled={magicLinkLoading || !email}
                    className="bg-purple-500 hover:bg-purple-600 text-white px-4"
                    data-testid="magic-link-btn"
                  >
                    {magicLinkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Link"}
                  </Button>
                </div>
              </div>

              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-3 bg-gradient-to-r from-orange-50 via-amber-50 to-yellow-50 text-gray-400">Or use password</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 text-sm">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-11 border-orange-200 focus:border-orange-400 focus:ring-orange-200 bg-white/80"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-700 text-sm">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 h-11 border-orange-200 focus:border-orange-400 focus:ring-orange-200 bg-white/80"
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-orange-500 via-red-500 to-amber-500 hover:from-orange-600 hover:via-red-600 hover:to-amber-600 text-white w-full h-11 rounded-xl shadow-lg shadow-orange-500/30"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
              </form>
            </>
          )}

          <p className="mt-8 text-center text-gray-600">
            Do not have an account?{" "}
            <Link to="/register" className="text-orange-600 font-medium hover:text-orange-700">
              Sign up
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Diverse Images Gallery with gradient overlay */}
      <div className="hidden lg:block lg:w-1/2 relative">
        {/* Warm sunset gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-400/90 via-orange-400/90 to-yellow-300/90 z-10"></div>
        
        {/* Image collage */}
        <div className="absolute inset-0 grid grid-cols-2 gap-2 p-4 z-0">
          <div className="space-y-2">
            <div className="rounded-2xl overflow-hidden h-[45%]">
              <img 
                src={sideImages[0].src}
                alt={sideImages[0].alt}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden h-[53%]">
              <img 
                src={sideImages[2].src}
                alt={sideImages[2].alt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="pt-12">
            <div className="rounded-2xl overflow-hidden h-[85%]">
              <img 
                src={sideImages[1].src}
                alt={sideImages[1].alt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Content overlay */}
        <div className="absolute inset-0 flex items-center justify-center z-20 p-12">
          <div className="text-center text-white">
            <h2 className="font-display text-4xl font-bold mb-4 drop-shadow-lg">Welcome Back</h2>
            <p className="text-white/90 text-lg max-w-md drop-shadow-md">
              Your clients are waiting. Let&apos;s get back to creating beautiful styles.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
